import React, { useState, useMemo, useEffect, useRef } from "react";
import { AgGridReact } from "ag-grid-react";
import { ColDef, GridApi, SideBarDef } from "ag-grid-community";
import { toast } from "react-toastify";
import {
  VStack,
  HStack,
  ButtonPrimary,
  ButtonPrimary1,
  Box,
} from "../../../../components/utils";
import { Input } from "../../../../components/shared/InputField";
import SearchInput from "../../../../components/shared/SearchInput";
import {
  AllocateSharesReq,
  EmployeeOfferDetails,
  MonetaryDetailsV2,
} from "../../../../types/Transaction";
import { useGetSecurityClasses } from "../../../../queries/securityClass";
import { Select } from "../../../../components/shared/Select";
import Avatar from "../../../esopOverview/Avatar";
import {
  formatCurrency,
  getCurrencySymbol,
  getCurrencyType,
} from "../../../../utils/currencyFormatter";
import { formatDisplayDate } from "../../../../utils/date";
import TransactionStatusLabel from "./TransactionStatusLabel";
import { SecurityClass } from "../../../companyProfile/AddOrEditSecurityClass";
import { allocateSharesToEmployees } from "../../../../api/TransactionApisV2";

const AllocateSharesDialog = ({
  employees = [],
  onClose,
}: {
  employees?: EmployeeOfferDetails[];
  onClose: () => void;
}) => {
  const currencySymbol = getCurrencySymbol();
  const currencyType = getCurrencyType();
  const { data: securityClasses } = useGetSecurityClasses();
  const [allocationDate, setAllocationDate] = useState("");
  const [exerciseDate, setExerciseDate] = useState("");
  const [selectedSecurityClass, setSelectedSecurityClass] =
    useState<string>("");
  const [employeeRows, setEmployeeRows] = useState(() =>
    employees.map((emp) => ({ ...emp, exerciseDate: emp.exerciseDate || "" }))
  );
  const [error, setError] = useState<string>("");

  // Helper to check if any employee has payment enabled
  const anyEmployeeHasExercisePayment = employeeRows.some(
    (emp) =>
      emp.monetaryTransactionDetails?.monetaryDetails
        ?.transactionPaymentEnabled &&
      (emp.monetaryTransactionDetails?.monetaryDetails
        ?.hissaGeneratedPaymentEnabled ||
        emp.monetaryTransactionDetails?.monetaryDetails?.paymentLink)
  );
  const anyEmployeeHasTaxPayment = employeeRows.some(
    (emp) =>
      emp.monetaryTransactionDetails?.monetaryDetails
        ?.transactionPaymentEnabled &&
      (emp.monetaryTransactionDetails?.monetaryDetails
        ?.hissaGeneratedTaxPaymentEnabled ||
        emp.monetaryTransactionDetails?.monetaryDetails?.taxPaymentLink)
  );

  function PreviewListStatus(status: any) {
    return (
      <HStack>
        <TransactionStatusLabel state={status} />
      </HStack>
    );
  }

  function EmployeeInfoRender(emp: any) {
    return (
      <HStack>
        <Avatar
          name={emp.data.employeeName}
          status={emp.data.employmentStatus}
        />
        <Box className="ml-2">
          <p className="text-xs font-medium text-gray-dark">
            {emp.data.employeeName}
          </p>
          <p className="capitalize text-xxs text-gray-light">
            {emp.data.hrId || ""}
          </p>
        </Box>
      </HStack>
    );
  }

  function RequestedCellRender(params: any) {
    const empMonetaryDetails =
      params.data.monetaryTransactionDetails?.monetaryDetails || {};
    const percentage = empMonetaryDetails.percentageOfNetVestedOptions || 0;
    const totalEligibleOptions = params.data.totalEligibleOptions || 0;
    const willingness = params.data.totalWillingness || 0;
    const eligible =
      percentage > 0
        ? Math.floor(totalEligibleOptions * (percentage / 100))
        : totalEligibleOptions;
    return (
      <VStack className="items-center justify-center gap-1">
        <p className="text-xs font-medium text-gray-dark">{willingness}</p>
        <p className="text-xs font-medium text-gray-400">
          {eligible
            ? parseFloat(((willingness / eligible) * 100).toFixed(4))
            : 0}
          %
        </p>
      </VStack>
    );
  }

  function EventNameRender(params: any) {
    const empMonetaryTransactionDetails =
      params.data.monetaryTransactionDetails;
    return (
      <VStack className="justify-center gap-1 ">
        <p className="text-xs font-medium text-gray-dark">
          {empMonetaryTransactionDetails.eventName}
        </p>
        <p className="text-xs font-medium text-gray-400">
          {formatDisplayDate(empMonetaryTransactionDetails.startDate)} -{" "}
          {formatDisplayDate(empMonetaryTransactionDetails.endDate)}
        </p>
      </VStack>
    );
  }

  // Payment columns
  const exercisePricePaid: ColDef | undefined = anyEmployeeHasExercisePayment
    ? {
        headerName: "EXERCISE PRICE",
        autoHeight: true,
        field: "totalExercisePrice",
        flex: 1,
        width: 150,
        cellStyle: { paddingTop: "15px", lineHeight: "20px" },
        cellRenderer: (params: any) => {
          const empMonetaryDetails =
            params.data.monetaryTransactionDetails?.monetaryDetails;
          if (
            !empMonetaryDetails ||
            !empMonetaryDetails.transactionPaymentEnabled
          ) {
            return <span className="text-xs text-gray-400">NA</span>;
          }
          const paymentDetails = params.data.paymentDetails || [];
          const latestExercisePrice = paymentDetails.find(
            (p: any) => p.paymentType?.toLowerCase() === "exercise price"
          );
          const transactionCompensationDetails =
            params.data.transactionCompensationDetails;
          return (
            <VStack className="items-center justify-between gap-1">
              <p className="text-xs font-medium text-gray-dark">
                {formatCurrency(
                  transactionCompensationDetails?.totalStrikePrice || 0,
                  currencyType
                )}
              </p>
              <p
                className={`text-xs font-medium text-gray-400 ${
                  latestExercisePrice?.paymentStatus === "FAILED"
                    ? "text-red-500"
                    : latestExercisePrice?.paymentStatus === "SUCCESS"
                    ? "text-[#069F10]"
                    : "text-[#E29500]"
                }`}
              >
                {latestExercisePrice?.paymentStatus === "SUCCESS"
                  ? "PAID"
                  : latestExercisePrice?.paymentStatus === "FAILED"
                  ? "FAILED"
                  : latestExercisePrice?.paymentStatus || "PENDING"}
              </p>
            </VStack>
          );
        },
      }
    : undefined;

  const taxPaid: ColDef | undefined = anyEmployeeHasTaxPayment
    ? {
        headerName: "TAX PRICE",
        autoHeight: true,
        field: "totalTaxPrice",
        flex: 1,
        width: 150,
        cellStyle: { paddingTop: "15px", lineHeight: "20px" },
        cellRenderer: (params: any) => {
          const empMonetaryDetails =
            params.data.monetaryTransactionDetails?.monetaryDetails;
          if (
            !empMonetaryDetails ||
            !empMonetaryDetails.transactionPaymentEnabled
          ) {
            return <span className="text-xs text-gray-400">NA</span>;
          }
          const paymentDetails = params.data.paymentDetails || [];
          const latestTaxPrice = paymentDetails.find(
            (p: any) => p.paymentType?.toLowerCase() === "tax price"
          );
          const transactionCompensationDetails =
            params.data.transactionCompensationDetails;
          return (
            <VStack className="items-center justify-between gap-1">
              <p className="text-xs font-medium text-gray-dark">
                {formatCurrency(
                  transactionCompensationDetails?.totalTaxAmount || 0,
                  currencyType
                )}
              </p>
              <p
                className={`text-xs font-medium text-gray-400 ${
                  latestTaxPrice?.paymentStatus === "FAILED"
                    ? "text-red-500"
                    : latestTaxPrice?.paymentStatus === "SUCCESS"
                    ? "text-[#069F10]"
                    : "text-[#E29500]"
                }`}
              >
                {latestTaxPrice?.paymentStatus === "SUCCESS"
                  ? "PAID"
                  : latestTaxPrice?.paymentStatus === "FAILED"
                  ? "FAILED"
                  : latestTaxPrice?.paymentStatus || "PENDING"}
              </p>
            </VStack>
          );
        },
      }
    : undefined;

  const columnDefs: ColDef[] = [
    {
      headerName: "EMPLOYEE NAME",
      width: 200,
      autoHeight: true,
      cellStyle: { paddingTop: "15px", lineHeight: "20px" },
      wrapText: true,
      field: "employeeInfo",
      initialWidth: 200,
      minWidth: 200,
      cellRendererParams: (params: any) => ({
        employeeName: params.data.employeeName,
        employeeDesignation: params.data.employeeDesignation,
        employeeDepartment: params.data.employeeDepartment,
        employeeState: params.data.employeeState,
      }),
      cellRenderer: (params: any) => EmployeeInfoRender(params),
      getQuickFilterText: (params: any) =>
        [
          params.data.employeeName,
          params.data.employeeDesignation,
          params.data.employeeDepartment,
          params.data.employeeState,
        ]
          .filter(Boolean)
          .join(" "),
    },
    {
      headerName: "EVENT NAME",
      autoHeight: true,
      field: "eventName",
      flex: 1,
      width: 200,
      cellStyle: { paddingTop: "15px", lineHeight: "20px" },

      cellRenderer: (params: any) => EventNameRender(params),
    },
    {
      headerName: "WILLINGNESS",
      autoHeight: true,
      field: "totalWillingness",
      flex: 1,
      width: 150,
      cellStyle: { paddingTop: "15px", lineHeight: "20px" },

      cellRenderer: (params: any) => RequestedCellRender(params),
    },
    ...(exercisePricePaid ? [exercisePricePaid] : []),
    ...(taxPaid ? [taxPaid] : []),
    {
      headerName: "EXERCISE DATE",
      autoHeight: true,
      field: "exerciseDate",
      flex: 1,
      width: 150,
      cellRenderer: (params: any) =>
        params.data.exerciseDate ? (
          formatDisplayDate(params.data.exerciseDate)
        ) : (
          <span className="text-gray-400">-</span>
        ),
    },
    {
      headerName: "STATUS",
      autoHeight: true,
      field: "offerState",
      flex: 1,
      width: 80,
      cellStyle: { paddingTop: "15px", lineHeight: "20px" },
      cellRendererParams: (params: any) => ({
        params,
      }),
      cellRenderer: (params: any) => PreviewListStatus(params.value),
    },
  ];

  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: true,
      wrapText: true,
      flex: 1,
      autoHeight: true,
      initialWidth: 150,
      wrapHeaderText: true,
      autoHeaderHeight: true,
      cellClass: "multiline",
      columnsMenuParams: {
        suppressColumnFilter: true,
      },
      filterParams: {
        buttons: ["reset"],
        maxNumConditions: 5,
      },
      minWidth: 150,
      filter: true,
      resizable: true,
    }),
    []
  );

  const sideBar = useMemo<SideBarDef>(
    () => ({
      toolPanels: [
        {
          id: "columns",
          labelDefault: "Columns",
          labelKey: "columns",
          iconKey: "columns",
          toolPanel: "agColumnsToolPanel",
          minWidth: 225,
          width: 225,
          maxWidth: 225,
          toolPanelParams: {
            suppressRowGroups: true,
            suppressPivots: true,
            suppressPivotMode: true,
            suppressValues: true,
          },
        },
        {
          id: "filters",
          labelDefault: "Filters",
          labelKey: "filters",
          iconKey: "filter",
          toolPanel: "agFiltersToolPanel",
          minWidth: 180,
          maxWidth: 400,
          width: 250,
        },
      ],
    }),
    []
  );

  const gridApi = useRef<GridApi | any>(null);
  const onGridReady = (params: any) => {
    gridApi.current = params;
  };

  const handleExerciseDateChange = (val: string) => {
    setExerciseDate(val);
    setEmployeeRows((prev) =>
      prev.map((row) => {
        const empMonetaryDetails =
          row.monetaryTransactionDetails?.monetaryDetails;
        const hasExercisePayment =
          empMonetaryDetails?.transactionPaymentEnabled &&
          (empMonetaryDetails?.hissaGeneratedPaymentEnabled ||
            empMonetaryDetails?.paymentLink);
        if (!hasExercisePayment) {
          return { ...row, exerciseDate: val };
        }
        return row;
      })
    );
  };

  const [isSaving, setIsSaving] = useState(false);

  const handleSave = () => {
    if (!isValid) {
      setError(
        "All employees must have exercise date and allocation date is required."
      );
      return;
    }
    setError("");
    setIsSaving(true);
    const employeeDetails: AllocateSharesReq[] = employeeRows.map((emp) => ({
      employeeId: emp.employeeId,
      hrId: emp.hrId,
      requestId: emp.requestId || "",
      transactionId: emp.transactionId || "",
      exerciseDate: new Date(emp.exerciseDate),
      allocationDate: new Date(allocationDate),
      securityClassId: selectedSecurityClass,
    }));
    allocateSharesToEmployees({ employeeDetails })
      .then(() => {
        toast.success("Shares allocated successfully!");
        onClose();
      })
      .catch((err) => {
        setError(err?.response?.data?.errorRaw || "Failed to allocate shares.");
      })
      .finally(() => setIsSaving(false));
  };

  const [showExerciseDate, setShowExerciseDate] = useState(false);

  // Validation
  const isValid =
    allocationDate &&
    (!showExerciseDate || employeeRows.every((row) => row.exerciseDate)) &&
    (securityClasses?.length ? selectedSecurityClass : true);

  useEffect(() => {
    employeeRows.forEach((row) => {
      if (!row.exerciseDate) {
        setShowExerciseDate(true);
      }
    });
  }, []);

  return (
    <div className="w-full bg-white rounded-lg gap-2">
      <div className="px-8 py-6 text-lg font-medium border-b">
        <h6 className="flex justify-between">
          {"Allocate Shares for "}
          {employees?.length}
          {" Employees"}
          <span className="cursor-pointer" onClick={onClose}>
            X
          </span>
        </h6>
      </div>
      <VStack className="gap-4 p-6">
        <HStack className="gap-4">
          <div className="flex-1">
            <label className="block mb-1 text-sm font-medium">
              Allocation Date
            </label>
            <Input
              type="date"
              value={allocationDate}
              onChange={(e) => setAllocationDate(e.target.value)}
            />
          </div>
          {showExerciseDate ? (
            <div className="flex-1">
              <label className="block mb-1 text-sm font-medium">
                Exercise Date
              </label>
              <Input
                type="date"
                value={exerciseDate}
                onChange={(e) => handleExerciseDateChange(e.target.value)}
              />
            </div>
          ) : (
            <div className="flex-1"> </div>
          )}
        </HStack>
        {securityClasses && securityClasses.length > 0 && (
          <div>
            <label className="block mb-1 text-sm font-medium">
              Security Class
            </label>
            <Select
              options={securityClasses}
              textGetter={(item: SecurityClass) =>
                `Name: ${item.securityName} | ISIN: ${item.isin} | Class:  ${item.securityClass}`
              }
              valueGetter={(item: SecurityClass) => item.id}
              value={selectedSecurityClass}
              onChange={(e) => setSelectedSecurityClass(e.target.value)}
              placeholder="Select Security Class"
            />
          </div>
        )}
        <div className="flex gap-2 text-xs text-center justify-evenly">
          <SearchInput
            className=""
            placeholder={`Search`}
            onChange={(e: any) => {
              gridApi.current?.api.setQuickFilter(e.target.value);
            }}
          />
        </div>
        <HStack className="justify-between w-full pt-4">
          <Box
            style={{
              height: "400px",
            }}
            className="w-full h-full max-h-full overflow-x-auto bg-black ag-theme-material"
          >
            <AgGridReact
              rowData={employeeRows}
              onGridReady={onGridReady}
              rowSelection="multiple"
              columnDefs={columnDefs}
              suppressRowTransform={true}
              sideBar={sideBar}
              defaultColDef={defaultColDef}
              animateRows={true}
              suppressRowClickSelection
              suppressMenuHide={true}
              rowClass={
                "border-t border-dashed cursor-pointer hover:bg-slate-50 "
              }
              overlayNoRowsTemplate={
                '<span style="padding: 10px; border: 2px solid #444; background: lightgoldenrodyellow;   margin-top: 50px;">No Rows To Show</span>'
              }
            ></AgGridReact>
          </Box>
        </HStack>
        {error && <div className="text-sm text-red-500">{error}</div>}
        <HStack className="justify-between gap-2 pt-4">
          <ButtonPrimary1 onClick={onClose} disabled={isSaving}>
            Cancel
          </ButtonPrimary1>
          <ButtonPrimary
            onClick={handleSave}
            disabled={!isValid || isSaving}
            loading={isSaving}
          >
            Save
          </ButtonPrimary>
        </HStack>
      </VStack>
    </div>
  );
};

export default AllocateSharesDialog;
